/**
 * Evaluation templates list. Template example: NextAdmin `(dashboard)/(home)/_component/last-transactions-table`
 * and demo `/tables/data-tables` (the `ListPage` + `DataTable` template); the create panel follows
 * `profile/account/page.tsx` (`FormSection` + `FormActions`).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ClipboardList, Globe, Lock, Plus } from 'lucide-react';
import { listEvaluationTemplatesOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Callout, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { PAGE_HELP } from '../../help/index.js';
import type { EvaluationTemplate } from '../../services/api/evaluation-template.api.js';
import { TemplateDrawer } from './evaluation-templates/TemplateDrawer.js';

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  active: 'success',
  draft: 'warning',
  deprecated: 'neutral',
  retired: 'neutral',
};

const COLUMNS: DataTableColumn<EvaluationTemplate>[] = [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    sortValue: (t) => t.name,
    render: (t) => (
      <div className="min-w-0">
        <p className="font-medium text-heading">{t.name}</p>
        <p className="font-mono text-xs text-muted">{t.templateRef}</p>
        {t.description && <p className="max-w-xs truncate text-xs text-muted">{t.description}</p>}
      </div>
    ),
  },
  { key: 'subjectKind', header: 'Subject kind', render: (t) => <Badge variant="neutral">{t.subjectKind}</Badge> },
  {
    key: 'status',
    header: 'Status',
    render: (t) => <Badge variant={STATUS_VARIANTS[t.status] ?? 'neutral'}>{t.status}</Badge>,
  },
  {
    key: 'visibility',
    header: 'Visibility',
    render: (t) => (
      <span className="flex items-center gap-1 text-xs text-muted">
        {t.visibility === 'org' ? <Globe size={12} className="text-accent-text" /> : <Lock size={12} />}
        {t.visibility === 'org' ? 'Org' : 'Private'}
      </span>
    ),
  },
  {
    key: 'versions',
    header: 'Versions',
    align: 'right',
    render: (t) => (t.currentVersionId != null ? '≥1' : '0'),
  },
];

export const EvaluationTemplatesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('template')) || null;
  const help = PAGE_HELP['evaluation-templates'];

  const { data, isLoading, error, refetch } = useQuery(listEvaluationTemplatesOptions());
  const templates: EvaluationTemplate[] = data?.data ?? [];

  const selected = templates.find((t) => t.id === selectedId) ?? null;
  const setSelected = (template: EvaluationTemplate | null) =>
    setSearchParams(template ? { template: String(template.id) } : {}, { replace: true });

  const newButton = (
    <Link
      to="/admin/evaluation-templates/new"
      className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
    >
      <Plus size={14} aria-hidden="true" /> New template
    </Link>
  );

  return (
    <Can policy="CanManageEvaluationTemplates">
      <ListPage
        title="Evaluation templates"
        helpContent={help}
        actions={newButton}
        alert={
          error && !isLoading ? (
            <Callout
              tone="danger"
              icon={AlertTriangle}
              title="Failed to load evaluation templates"
              actions={
                <Button size="sm" variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            >
              Something went wrong fetching the templates. Check your connection and try again.
            </Callout>
          ) : undefined
        }
      >
        {!error && (
          <DataTable
            aria-label="Evaluation templates"
            columns={COLUMNS}
            rows={templates}
            rowKey={(t) => t.id}
            onRowClick={setSelected}
            loading={isLoading}
            pageSize={20}
            empty={
              <EmptyState
                icon={ClipboardList}
                title={help.emptyState.title}
                description={help.emptyState.description}
                learnItems={help.emptyState.learnItems}
                action={newButton}
              />
            }
          />
        )}
      </ListPage>
      {selected && <TemplateDrawer template={selected} onClose={() => setSelected(null)} />}
    </Can>
  );
};
